<template>
  <!-- Empty state: no location selected yet -->
  <div
    v-if="!hasLocation"
    class="w-full min-h-[316px] border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center gap-3 bg-white cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors"
    @click="$emit('open-map-dialog', disableAutoPin)"
  >
    <i class="pi pi-map-marker text-4xl text-gray-400" />
    <div class="text-center">
      <p class="font-semibold text-gray-700 m-0">{{ emptyTitle }}</p>
      <p class="text-sm text-gray-400 mt-1 mb-0">{{ emptySubtitle }}</p>
    </div>
  </div>

  <!-- Selected state: location set — shows embedded map + coords -->
  <div
    v-else
    class="w-full border-2 border-green-500 rounded-xl overflow-hidden bg-white"
  >
    <div
      ref="mapContainer"
      class="w-full h-64"
    />
    <div
      class="flex items-center justify-between px-4 py-3 border-t border-gray-100"
    >
      <div>
        <p class="text-xs uppercase tracking-wide text-gray-500 m-0">
          GPS Location
        </p>
        <p class="text-sm font-semibold text-green-700 mt-0.5 mb-0">
          {{ location.latitude.toFixed(6) }},
          {{ location.longitude.toFixed(6) }}
        </p>
      </div>
      <Button
        label="Update Location"
        icon="pi pi-map"
        severity="secondary"
        size="small"
        @click="$emit('open-map-dialog', disableAutoPin)"
      />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, nextTick, onUnmounted } from "vue";

  const props = defineProps({
    location: {
      type: Object,
      default: null,
      validator: (v) =>
        v === null ||
        (typeof v.latitude === "number" && typeof v.longitude === "number")
    },
    emptyTitle: {
      type: String,
      default: "No location selected"
    },
    emptySubtitle: {
      type: String,
      default: "Tap to open map and drop a pin"
    },
    disableAutoPin: {
      type: Boolean,
      default: false
    }
  });

  defineEmits(["open-map-dialog"]);

  const mapContainer = ref(null);
  let map = null;
  let marker = null;

  const hasLocation = computed(
    () =>
      props.location != null &&
      props.location.latitude != null &&
      props.location.longitude != null
  );

  const initMap = async () => {
    await nextTick();
    if (!mapContainer.value) return;

    if (!window.google || !window.google.maps) {
      if (window.loadGoogleMapsAPI) {
        try {
          await window.loadGoogleMapsAPI();
        } catch (e) {
          console.error("Failed to load Google Maps API:", e);
          return;
        }
      } else {
        console.warn("Google Maps API not available");
        return;
      }
    }

    if (!window.google.maps.marker?.AdvancedMarkerElement) {
      console.error("AdvancedMarkerElement not available");
      return;
    }

    const center = {
      lat: props.location.latitude,
      lng: props.location.longitude
    };

    map = new window.google.maps.Map(mapContainer.value, {
      center,
      zoom: 15,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      mapId: import.meta.env.VITE_GMAPS_MAP_ID
    });

    if (marker) marker.map = null;

    marker = new window.google.maps.marker.AdvancedMarkerElement({
      position: center,
      map,
      title: "Selected Location",
      gmpDraggable: false
    });
  };

  // Initialize map once the DOM container mounts (v-else branch becomes visible)
  watch(mapContainer, (el) => {
    if (el && hasLocation.value) {
      initMap();
    }
  });

  // When location changes after map is already initialized, just reposition
  watch(
    () => props.location,
    (newLoc) => {
      if (!newLoc || newLoc.latitude == null || newLoc.longitude == null)
        return;
      if (map) {
        // Map already exists — reposition without rebuilding
        const pos = { lat: newLoc.latitude, lng: newLoc.longitude };
        map.setCenter(pos);
        if (marker) marker.map = null;
        marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: pos,
          map,
          title: "Selected Location",
          gmpDraggable: false
        });
      }
      // If map doesn't exist yet, mapContainer watcher will trigger initMap
    }
  );

  onUnmounted(() => {
    if (marker) {
      marker.map = null;
      marker = null;
    }
    map = null;
  });
</script>
